import { FC, useEffect, useId, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import CaresPageHeader from '../shell/cares-page-header';
import { getCaresShellBootstrap, type CaresShellGroupScope } from '../shell/bootstrap';
import {
  fetchGroupTallies,
  type GroupTalliesCard,
  type GroupTalliesDimension,
  type GroupTalliesMode,
  type GroupTalliesResponse,
} from './group-tallies-api';

type GroupOption = {
  id: number;
  name: string;
  slug: string;
  companyName: string | null;
  companySlug: string | null;
};

type LoadState =
  | { status: 'idle' | 'loading' }
  | { status: 'success'; data: GroupTalliesResponse }
  | { status: 'error'; message: string };

const collectVisibleGroups = (): GroupOption[] => {
  const bootstrap = getCaresShellBootstrap();
  const seen = new Set<number>();
  const groups: GroupOption[] = [];

  bootstrap.tenantCompanies.forEach((company) => {
    company.groups.forEach((group: CaresShellGroupScope) => {
      if (group.status !== 'active' || group.kind === 'department' || seen.has(group.id)) {
        return;
      }

      seen.add(group.id);
      groups.push({
        id: group.id,
        name: group.name,
        slug: group.slug,
        companyName: group.company?.name ?? company.name,
        companySlug: group.company?.slug ?? company.slug,
      });
    });
  });

  return groups.sort((left, right) =>
    left.name.localeCompare(right.name) || left.slug.localeCompare(right.slug)
  );
};

const formatMetricValue = (value: number | null, unitLabel?: string) => {
  if (value === null) {
    return 'Unavailable';
  }

  const formatted = Number.isInteger(value)
    ? value.toLocaleString()
    : value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });

  return unitLabel ? `${formatted} ${unitLabel}` : formatted;
};

const formatPercent = (value: number | null) => {
  if (value === null) {
    return 'Unavailable';
  }

  return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
};

const formatDelta = (value: number | null, unitLabel: string) => {
  if (value === null) {
    return 'Unavailable';
  }

  const rendered = Number.isInteger(value) ? value.toString() : value.toFixed(2);
  return `${value > 0 ? '+' : ''}${rendered} ${unitLabel}`;
};

const toneForSeverity = (severity: string) => {
  switch (severity) {
    case 'alert':
      return 'danger';
    case 'warning':
      return 'warning';
    default:
      return 'success';
  }
};

const labelForSeverity = (severity: string) => {
  switch (severity) {
    case 'alert':
      return 'Variance alert';
    case 'warning':
      return 'Variance warning';
    default:
      return 'Within benchmark';
  }
};

const renderWindowSummary = (dimension: GroupTalliesDimension) => {
  const parts = [
    dimension.windowSummary.periodType,
    dimension.windowSummary.periodStart,
    dimension.windowSummary.periodEnd,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' · ') : 'Window details were not supplied by the current API response.';
};

const findDimension = (card: GroupTalliesCard, dimensions: GroupTalliesDimension[]) =>
  dimensions.find((dimension) => dimension.dimensionKey === card.cardKey) ?? null;

const readOnlyBoundaryNote =
  'Authorization, visible departments, comparison modes, cohort eligibility, normalization, and suppression all come from the server. This page formats the resolved payload only.';

const currentMonth = () => new Date().toISOString().slice(0, 7);
const currentYear = () => String(new Date().getFullYear());
const currentFiscalYear = () => {
  const today = new Date();
  return String(today.getMonth() >= 6 ? today.getFullYear() : today.getFullYear() - 1);
};
const currentMonthRange = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const dateString = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return {
    start: dateString(new Date(year, month, 1)),
    end: dateString(new Date(year, month + 1, 0)),
  };
};

const LoadingCards: FC = () => (
  <Row className="g-4">
    {[0, 1, 2].map((index) => (
      <Col xl={4} md={6} key={index}>
        <Card className="custom-card h-100">
          <Card.Body className="d-flex align-items-center gap-3">
            <Spinner animation="border" size="sm" />
            <div className="text-muted">Loading your published tally comparison.</div>
          </Card.Body>
        </Card>
      </Col>
    ))}
  </Row>
);

const GroupTalliesPage: FC = () => {
  const bootstrap = getCaresShellBootstrap();
  const location = useLocation();
  const schedulerWorkspace = location.pathname.startsWith('/scheduling/');
  const pageHeader = <CaresPageHeader homepage={schedulerWorkspace ? 'Scheduling' : 'My Schedule'} activepage={schedulerWorkspace ? 'Scheduling' : 'My Schedule'} page="Department Tallies" actions={<></>} />;
  const [searchParams, setSearchParams] = useSearchParams();
  const [loadState, setLoadState] = useState<LoadState>({ status: 'idle' });
  const [refreshTick, setRefreshTick] = useState(0);
  const [liveMessage, setLiveMessage] = useState('Loading Department Tallies.');
  const modeControlId = useId();
  const visibleGroups = collectVisibleGroups();
  const searchParamsValue = searchParams.toString();
  const requestedGroup = searchParams.get('group');
  const requestedMode = searchParams.get('mode');
  const requestedPeriod = searchParams.get('period');
  const requestedPeriodMode = searchParams.get('period_mode');
  const requestedPeriodValue = searchParams.get('period_value');
  const requestedPeriodStart = searchParams.get('period_start');
  const requestedPeriodEnd = searchParams.get('period_end');
  const requestedCompany = searchParams.get('company');
  const requestedLocation = searchParams.get('location');
  const requestedPhysician = searchParams.get('physician');
  const requestedService = searchParams.get('service');
  const requestedRole = searchParams.get('role');
  const requestedShift = searchParams.get('shift');
  const selectedGroupSlug = visibleGroups.some((group) => group.slug === requestedGroup)
    ? requestedGroup
    : visibleGroups[0]?.slug ?? null;

  useEffect(() => {
    if (!selectedGroupSlug) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    let changed = false;

    if (searchParams.get('group') !== selectedGroupSlug) {
      nextParams.set('group', selectedGroupSlug);
      changed = true;
    }

    if (changed) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParamsValue, selectedGroupSlug, setSearchParams]);

  useEffect(() => {
    if (!bootstrap.authenticated || !selectedGroupSlug) {
      return;
    }

    let cancelled = false;

    const loadTallies = async () => {
      setLoadState({ status: 'loading' });
      setLiveMessage('Loading Department Tallies.');

      try {
        // The browser only requests server-approved scope and mode choices.
        const response = await fetchGroupTallies({
          group: selectedGroupSlug,
          company: requestedCompany,
          period: requestedPeriod,
          periodMode: requestedPeriodMode,
          periodValue: requestedPeriodValue,
          periodStart: requestedPeriodStart,
          periodEnd: requestedPeriodEnd,
          mode: requestedMode,
          schedulerFilters: schedulerWorkspace,
          location: requestedLocation,
          physician: requestedPhysician,
          service: requestedService,
          role: requestedRole,
          shift: requestedShift,
        });

        if (cancelled) {
          return;
        }

        setLoadState({
          status: 'success',
          data: response,
        });
        setLiveMessage('Department Tallies updated.');

        const resolvedMode = response.appliedFilters.mode_key
          ?? response.availableModes.find((mode) => mode.isDefault)?.modeKey
          ?? response.availableModes[0]?.modeKey
          ?? null;
        const nextParams = new URLSearchParams(searchParams);
        let changed = false;

        if (resolvedMode && resolvedMode !== requestedMode) {
          nextParams.set('mode', resolvedMode);
          changed = true;
        }

        if (!requestedPeriodMode) {
          const resolvedPeriod = response.selectedPeriod?.id ?? null;
          const resolvedPeriodMode = response.appliedFilters.period_mode ?? null;
          const resolvedPeriodValue = response.appliedFilters.period_value ?? null;
          const resolvedPeriodStart = response.appliedFilters.period_start ?? null;
          const resolvedPeriodEnd = response.appliedFilters.period_end ?? null;

          if (resolvedPeriod && resolvedPeriod !== requestedPeriod) {
            nextParams.set('period', resolvedPeriod);
            changed = true;
          }

          if (!resolvedPeriod && requestedPeriod) {
            nextParams.delete('period');
            changed = true;
          }

          ([
            ['period_mode', resolvedPeriodMode],
            ['period_value', resolvedPeriodValue],
            ['period_start', resolvedPeriodStart],
            ['period_end', resolvedPeriodEnd],
          ] as const).forEach(([key, resolved]) => {
            if (resolved) {
              nextParams.set(key, resolved);
              changed = true;
            }
          });
        }

        if (changed) {
          nextParams.set('group', selectedGroupSlug);
          setSearchParams(nextParams, { replace: true });
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        setLoadState({
          status: 'error',
          message: error instanceof Error ? error.message : 'Department Tallies could not be loaded right now.',
        });
        setLiveMessage('Department Tallies failed to load.');
      }
    };

    void loadTallies();

    return () => {
      cancelled = true;
    };
  }, [bootstrap.authenticated, refreshTick, searchParamsValue, selectedGroupSlug, setSearchParams]);

  const clearScheduleFilters = (params: URLSearchParams) => {
    ['location', 'physician', 'service', 'role', 'shift'].forEach((key) => params.delete(key));
  };

  const handleGroupChange = (groupSlug: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('group', groupSlug);
    nextParams.delete('mode');
    nextParams.delete('period');
    nextParams.delete('period_mode');
    nextParams.delete('period_value');
    nextParams.delete('period_start');
    nextParams.delete('period_end');
    clearScheduleFilters(nextParams);
    setSearchParams(nextParams);
  };

  const handlePeriodModeChange = (periodMode: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('group', selectedGroupSlug ?? '');
    nextParams.set('period_mode', periodMode);
    nextParams.delete('period');
    nextParams.delete('period_start');
    nextParams.delete('period_end');
    if (periodMode === 'month') nextParams.set('period_value', currentMonth());
    else if (periodMode === 'calendar_year') nextParams.set('period_value', currentYear());
    else if (periodMode === 'fiscal_year') nextParams.set('period_value', currentFiscalYear());
    else {
      const range = currentMonthRange();
      nextParams.delete('period_value');
      nextParams.set('period_start', range.start);
      nextParams.set('period_end', range.end);
    }
    clearScheduleFilters(nextParams);
    setSearchParams(nextParams);
  };

  const handlePeriodFieldChange = (key: 'period_value' | 'period_start' | 'period_end', value: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (value) nextParams.set(key, value); else nextParams.delete(key);
    clearScheduleFilters(nextParams);
    setSearchParams(nextParams);
  };

  const handleModeChange = (modeKey: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('group', selectedGroupSlug ?? '');
    nextParams.set('mode', modeKey);
    setSearchParams(nextParams);
  };

  const handleScheduleFilterChange = (filterKey: string, value: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (value) nextParams.set(filterKey, value); else nextParams.delete(filterKey);
    setSearchParams(nextParams);
  };

  const resetScheduleFilters = () => {
    const nextParams = new URLSearchParams(searchParams);
    clearScheduleFilters(nextParams);
    setSearchParams(nextParams);
  };

  if (!bootstrap.authenticated) {
    return (
      <>
        {pageHeader}
        <Alert variant="danger" className="custom-card">
          This comparison view requires an authenticated CARES session.
          <Link to="/login" className="alert-link ms-1">
            Sign in
          </Link>
          to load your published tally comparisons.
        </Alert>
      </>
    );
  }

  if (visibleGroups.length === 0) {
    return (
      <>
        {pageHeader}
        <Alert variant="info" className="custom-card mb-0">
          No active departments are currently available for this account.
        </Alert>
      </>
    );
  }

  const response = loadState.status === 'success' ? loadState.data : null;
  const defaultMode = response?.availableModes.find((mode) => mode.isDefault) ?? response?.availableModes[0] ?? null;
  const selectedMode = response?.availableModes.find((mode) => mode.modeKey === response.appliedFilters.mode_key)
    ?? defaultMode
    ?? null;
  const selectedPeriodMode = requestedPeriodMode ?? response?.appliedFilters.period_mode ?? 'month';
  const selectedPeriodValue = requestedPeriodValue
    ?? response?.appliedFilters.period_value
    ?? (selectedPeriodMode === 'month' ? response?.selectedPeriod?.periodStart?.slice(0, 7) ?? currentMonth() : '');
  const selectedPeriodStart = requestedPeriodStart ?? response?.appliedFilters.period_start ?? '';
  const selectedPeriodEnd = requestedPeriodEnd ?? response?.appliedFilters.period_end ?? '';
  const filterOptions = (filterKey: string) =>
    response?.allowedFilterMetadata.find((filter) => filter.filterKey === filterKey)?.options ?? [];
  const comparisonDimensions = response
    ? response.comparisonCards
      .map((card) => findDimension(card, response.dimensions))
      .filter((dimension): dimension is GroupTalliesDimension => Boolean(dimension))
    : [];
  const emptyState = response
    && response.comparisonCards.length === 0;

  return (
    <>
      {pageHeader}

      <div className="visually-hidden" aria-live="polite">
        {liveMessage}
      </div>

      {loadState.status === 'success' && response?.freshness.isStale ? (
        <Alert variant="warning" className="custom-card">
          <div className="fw-semibold mb-1">Published tally freshness warning</div>
          <div>{response.freshness.message}</div>
          {response.freshness.latestPeriodLabel ? (
            <div className="mt-2 text-muted fs-13">
              Latest published period: {response.freshness.latestPeriodLabel}
            </div>
          ) : null}
        </Alert>
      ) : null}

      <Card className="custom-card">
        <Card.Body>
          <div className="border rounded-3 p-3 p-xl-4 bg-light">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2 mb-3">
              <h6 className="mb-0">Department tally filters</h6>
              <div className="d-flex flex-wrap gap-2">
                {schedulerWorkspace ? (
                  <Button
                    type="button"
                    variant="outline-light"
                    className="bg-transparent text-white border-white"
                    size="sm"
                    onClick={resetScheduleFilters}
                    disabled={!requestedLocation && !requestedPhysician && !requestedService && !requestedRole && !requestedShift}
                  >
                    Reset schedule filters
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="outline-light"
                  className="bg-transparent text-white border-white"
                  size="sm"
                  onClick={() => setRefreshTick((current) => current + 1)}
                  disabled={loadState.status === 'loading'}
                >
                  {loadState.status === 'loading' ? 'Refreshing…' : 'Refresh'}
                </Button>
                <Link to={schedulerWorkspace ? '/scheduling/overview' : '/my-schedule'} className="btn btn-light btn-sm">
                  Return to Overview
                </Link>
              </div>
            </div>
            <Row className="g-3 align-items-end">
            <Col xl={3} md={6}>
              <Form.Label htmlFor="group-tallies-group" className="text-uppercase text-muted fs-12 fw-semibold mb-2">Department</Form.Label>
              <Form.Select
                id="group-tallies-group"
                value={selectedGroupSlug ?? ''}
                onChange={(event) => handleGroupChange(event.target.value)}
                disabled={loadState.status === 'loading'}
              >
                {visibleGroups.map((group) => (
                  <option key={group.id} value={group.slug}>
                    {group.name} ({group.companySlug ?? group.companyName ?? 'company'})
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xl={3} md={6}>
              <Form.Label htmlFor="group-tallies-period-mode" className="text-uppercase text-muted fs-12 fw-semibold mb-2">Published period</Form.Label>
              <Form.Select
                id="group-tallies-period-mode"
                value={selectedPeriodMode}
                onChange={(event) => handlePeriodModeChange(event.target.value)}
                disabled={loadState.status === 'loading'}
              >
                <option value="month">Month</option>
                <option value="calendar_year">Calendar year (Jan–Dec)</option>
                <option value="fiscal_year">Fiscal year (Jul–Jun)</option>
                <option value="custom">Custom dates</option>
              </Form.Select>
            </Col>
            {selectedPeriodMode === 'month' ? (
              <Col xl={3} md={6}>
                <Form.Label htmlFor="group-tallies-period-value" className="text-uppercase text-muted fs-12 fw-semibold mb-2">Month</Form.Label>
                <Form.Control id="group-tallies-period-value" type="month" value={selectedPeriodValue} onChange={(event) => handlePeriodFieldChange('period_value', event.target.value)} />
              </Col>
            ) : null}
            {selectedPeriodMode === 'calendar_year' || selectedPeriodMode === 'fiscal_year' ? (
              <Col xl={3} md={6}>
                <Form.Label htmlFor="group-tallies-period-value" className="text-uppercase text-muted fs-12 fw-semibold mb-2">
                  {selectedPeriodMode === 'calendar_year' ? 'Calendar year' : 'Fiscal year starting'}
                </Form.Label>
                <Form.Select id="group-tallies-period-value" value={selectedPeriodValue} onChange={(event) => handlePeriodFieldChange('period_value', event.target.value)}>
                  {Array.from({ length: 21 }, (_, index) => String(new Date().getFullYear() - 10 + index)).map((year) => (
                    <option key={year} value={year}>
                      {selectedPeriodMode === 'fiscal_year' ? `FY ${year}–${Number(year) + 1}` : year}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            ) : null}
            {selectedPeriodMode === 'custom' ? (
              <>
                <Col xl={3} md={6}>
                  <Form.Label htmlFor="group-tallies-period-start" className="text-uppercase text-muted fs-12 fw-semibold mb-2">Start date</Form.Label>
                  <Form.Control id="group-tallies-period-start" type="date" value={selectedPeriodStart} max={selectedPeriodEnd || undefined} onChange={(event) => handlePeriodFieldChange('period_start', event.target.value)} />
                </Col>
                <Col xl={3} md={6}>
                  <Form.Label htmlFor="group-tallies-period-end" className="text-uppercase text-muted fs-12 fw-semibold mb-2">End date</Form.Label>
                  <Form.Control id="group-tallies-period-end" type="date" value={selectedPeriodEnd} min={selectedPeriodStart || undefined} onChange={(event) => handlePeriodFieldChange('period_end', event.target.value)} />
                </Col>
              </>
            ) : null}
            <Col xl={3} md={6}>
              <Form.Label htmlFor={modeControlId} className="text-uppercase text-muted fs-12 fw-semibold mb-2">Comparison mode</Form.Label>
              <Form.Select
                id={modeControlId}
                value={selectedMode?.modeKey ?? ''}
                onChange={(event) => handleModeChange(event.target.value)}
                disabled={loadState.status !== 'success' || (response?.availableModes.length ?? 0) <= 1}
              >
                {(response?.availableModes ?? []).map((mode: GroupTalliesMode) => (
                  <option key={mode.modeKey} value={mode.modeKey}>
                    {mode.modeLabel}
                  </option>
                ))}
              </Form.Select>
            </Col>
            {schedulerWorkspace ? (
              <>
                {[
                  ['location', 'Location', requestedLocation],
                  ['physician', 'Physician', requestedPhysician],
                  ['service', 'Service', requestedService],
                  ['role', 'Role', requestedRole],
                  ['shift', 'Shift', requestedShift],
                ].map(([filterKey, label, value]) => {
                  const options = filterOptions(filterKey);
                  return (
                    <Col xl={2} md={4} sm={6} key={filterKey}>
                      <Form.Label htmlFor={`group-tallies-${filterKey}`} className="text-uppercase text-muted fs-12 fw-semibold mb-2">{label}</Form.Label>
                      <Form.Select
                        id={`group-tallies-${filterKey}`}
                        value={value ?? ''}
                        onChange={(event) => handleScheduleFilterChange(filterKey, event.target.value)}
                        disabled={loadState.status !== 'success' || options.length === 0}
                      >
                        <option value="">All {label.toLowerCase()}</option>
                        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </Form.Select>
                    </Col>
                  );
                })}
              </>
            ) : null}
            </Row>
          </div>
        </Card.Body>
      </Card>

      {loadState.status === 'error' ? (
        <Alert variant="danger" className="custom-card">
          <div className="fw-semibold mb-1">Department Tallies could not be loaded right now.</div>
          <div className="mb-3">{loadState.message}</div>
          <Button type="button" variant="outline-danger" onClick={() => setRefreshTick((current) => current + 1)}>
            Retry
          </Button>
        </Alert>
      ) : null}

      {loadState.status === 'loading' ? <LoadingCards /> : null}

      {loadState.status === 'success' && emptyState ? (
        <Alert variant="info" className="custom-card">
          No tally comparisons are published for this selection yet.
        </Alert>
      ) : null}

      {loadState.status === 'success' && response && response.comparisonCards.length > 0 ? (
        <Row className="g-4">
          {response.comparisonCards.map((card) => {
            const dimension = findDimension(card, response.dimensions);
            const severity = dimension?.severity ?? 'normal';
            const comparisonLabel = card.comparisonGroupLabel ?? card.subtitle;

            return (
              <Col xl={4} md={6} key={card.cardKey}>
                <Card className="custom-card h-100">
                  <Card.Body className="d-flex flex-column gap-3">
                    <div className="d-flex align-items-start justify-content-between gap-3">
                      <div>
                        <div className="text-muted text-uppercase fs-12 fw-semibold">{card.title}</div>
                        <h4 className="mb-1">{formatMetricValue(dimension?.personValue ?? null, dimension?.unitLabel)}</h4>
                        <div className="text-muted fs-13">{card.subtitle}</div>
                      </div>
                      <Badge bg="" className={`bg-${toneForSeverity(severity)}-transparent text-${toneForSeverity(severity)}`}>
                        {labelForSeverity(severity)}
                      </Badge>
                    </div>

                    <div className="border rounded p-3">
                      <div className="text-muted fs-12 text-uppercase fw-semibold mb-1">Benchmark</div>
                      <div className="fw-semibold">{dimension?.comparisonBasis ?? comparisonLabel}</div>
                      <div className="text-muted fs-13 mt-1">
                        {comparisonLabel}
                        {card.comparisonGroupNamed ? ' · Named result approved by policy' : ' · Policy-approved anonymized cohort'}
                      </div>
                    </div>

                    <Row className="g-3">
                      <Col xs={6}>
                        <div className="text-muted fs-12 text-uppercase fw-semibold">Comparison value</div>
                        <div className="fw-semibold">{formatMetricValue(dimension?.cohortValue ?? null, dimension?.unitLabel)}</div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-muted fs-12 text-uppercase fw-semibold">Target value</div>
                        <div className="fw-semibold">{formatMetricValue(dimension?.targetValue ?? null, dimension?.unitLabel)}</div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-muted fs-12 text-uppercase fw-semibold">Variance</div>
                        <div className="fw-semibold">{formatDelta(dimension?.varianceValue ?? null, dimension?.unitLabel ?? 'units')}</div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-muted fs-12 text-uppercase fw-semibold">Variance percent</div>
                        <div className="fw-semibold">{formatPercent(dimension?.variancePercent ?? null)}</div>
                      </Col>
                    </Row>

                    <div className="pt-2 border-top">
                      <div className="text-muted fs-12 text-uppercase fw-semibold mb-1">Normalization</div>
                      <div className="fs-13">
                        {dimension?.normalizationSummary.inputs.length
                          ? dimension.normalizationSummary.inputs.join(', ')
                          : 'No normalization inputs supplied.'}
                      </div>
                      <div className="text-muted fs-12 mt-2">
                        Value source: {dimension?.normalizationSummary.valueSource ?? 'Unavailable'}
                      </div>
                    </div>

                    <div className="text-muted fs-12">
                      Window: {dimension ? renderWindowSummary(dimension) : 'Window details unavailable.'}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : null}

      {loadState.status === 'success' && response && comparisonDimensions.length > 0 ? (
        <Card className="custom-card">
          <Card.Header>
            <Card.Title>How to read this page</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col lg={4}>
                <div className="text-muted text-uppercase fs-12 fw-semibold mb-2">Benchmark labels</div>
                <div className="fs-14">
                  {selectedMode?.modeLabel ?? 'Current mode'} compares your published tally values against the benchmark label shown on each card.
                </div>
              </Col>
              <Col lg={4}>
                <div className="text-muted text-uppercase fs-12 fw-semibold mb-2">Normalization</div>
                <div className="fs-14">
                  Inputs such as FTE, fairness weight, and policy adjustments are rendered exactly as supplied by the server. Missing values stay unavailable instead of rendering as zero.
                </div>
              </Col>
              <Col lg={4}>
                <div className="text-muted text-uppercase fs-12 fw-semibold mb-2">Privacy boundary</div>
                <div className="fs-14">{readOnlyBoundaryNote}</div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      ) : null}
    </>
  );
};

export default GroupTalliesPage;
